import { HelixIcon } from "@helix/helix-icon";
import {
  smiley_negative_two as DislikeIcon,
  smiley_negative_one as HateIcon,
  smiley_positive_one as LikeIcon,
  smiley_positive_two as LoveIcon,
  smiley_neutral as MehIcon
} from "@helix/helix-icon/outlined";
import { Modal } from "@wrstudios/components";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Action,
  Actions,
  Button,
  Checkbox,
  Comment,
  Emoticon,
  Emoticons,
  Hint,
  ModalContent,
  ModalHeader,
  TextArea
} from "./styled/feedback-modal";

const FeedbackRating = {
  HATE: "hate",
  DISLIKE: "dislike",
  MEH: "meh",
  LIKE: "like",
  LOVE: "love"
};

const feedbackOptions = [
  {
    label: "hate",
    icon: HateIcon,
    value: FeedbackRating.HATE
  },
  {
    label: "dislike",
    icon: DislikeIcon,
    value: FeedbackRating.DISLIKE
  },
  {
    label: "meh",
    icon: MehIcon,
    value: FeedbackRating.MEH
  },
  {
    label: "like",
    icon: LikeIcon,
    value: FeedbackRating.LIKE
  },
  {
    label: "love",
    icon: LoveIcon,
    value: FeedbackRating.LOVE
  }
];

function FeedbackModal({
  reportId,
  type,
  loading,
  children,
  onCancel,
  onConfirm,
  onClose,
  ...props
}) {
  const [selectedRating, setSelectedRating] = useState(null);
  const [comment, setComment] = useState("");
  const [canBeContacted, setCanBeContacted] = useState(false);
  const [cancelFeedback, setCancelFeedback] = useState(false);

  const maxCommentChars = 1000;
  const hasSelectedRating = selectedRating !== null;
  const localStorageKey = `${type}_cfb`;

  const commentIsRequired =
    (selectedRating === FeedbackRating.DISLIKE ||
      selectedRating === FeedbackRating.HATE) &&
    !comment.trim().length;
  const disableSubmitButton = loading || commentIsRequired;

  useEffect(() => {
    requestAnimationFrame(() => {
      document.activeElement?.blur();
    });
  }, []);

  useEffect(() => {
    if (reportId) {
      const storedCancelFeedback = window.localStorage.getItem(localStorageKey);
      if (storedCancelFeedback && storedCancelFeedback === reportId) {
        setCancelFeedback(true);
      } else {
        setCancelFeedback(false);
        window.localStorage.removeItem(localStorageKey);
      }
    }
  }, [reportId]);

  if (cancelFeedback) {
    return null;
  }

  return (
    <Modal {...props} onClose={onClose}>
      <ModalContent>
        <ModalHeader>How satisfied are you with {type}?</ModalHeader>
        <Modal.Body>
          <Emoticons>
            {feedbackOptions.map((option) => (
              <Emoticon
                key={option.value}
                active={selectedRating === option.value}
                onClick={() => setSelectedRating(option.value)}>
                <HelixIcon width={25} height={25} icon={option.icon} />
                <span>{option.label}</span>
              </Emoticon>
            ))}
          </Emoticons>
          {hasSelectedRating && (
            <>
              {commentIsRequired && <Hint>Comment is required</Hint>}
              <Comment>
                <TextArea
                  autoFocus
                  placeholder={`Tell us about your experience with ${type}...`}
                  maxLength={maxCommentChars}
                  onChange={(evt) => {
                    const newText = evt.target.value;
                    if (newText.length <= maxCommentChars) {
                      setComment(newText);
                    }
                  }}
                />
                <p>{`${
                  !!comment.length
                    ? `${comment.length}/${maxCommentChars} Characters`
                    : `1000 Character Max`
                }`}</p>
              </Comment>
              <Checkbox
                onChange={(evt) => setCanBeContacted(evt.target.checked)}>
                <label>
                  <input type="checkbox" />
                  <div />
                  <span>Can we contact you?</span>
                </label>
              </Checkbox>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Actions>
            <Action>
              <Button
                variant="tertiary"
                onClick={() => {
                  setCancelFeedback(true);
                  window.localStorage.setItem(localStorageKey, reportId);
                  onCancel();
                  onClose();
                }}>
                Cancel
              </Button>
            </Action>
            {hasSelectedRating && (
              <Action>
                <Button
                  app="cma"
                  disabled={disableSubmitButton}
                  loading={loading}
                  onClick={() => {
                    onConfirm({
                      feature: type.toLowerCase(),
                      rating: selectedRating,
                      comment: comment || null,
                      consent_to_contact: canBeContacted
                    });
                    onClose();
                  }}>
                  Send Feedback
                </Button>
              </Action>
            )}
          </Actions>
        </Modal.Footer>
      </ModalContent>
    </Modal>
  );
}

FeedbackModal.defaultProps = {
  reportId: null,
  loading: false,
  onCancel: () => {},
  onConfirm: () => {},
  onClose: () => {}
};

FeedbackModal.propTypes = {
  reportId: PropTypes.string,
  loading: PropTypes.bool,
  type: PropTypes.oneOf(["Presentations"]),
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func
};

export default FeedbackModal;
